import { COGNITO_DOMAIN, CLIENT_ID } from '../config/constants';
import { storage, STORAGE_KEYS } from '../utils/storage';

/**
 * Authentication service
 */
export const authService = {
  /**
   * Get stored auth token
   */
  getToken: () => {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Get stored user info
   */
  getUserInfo: () => {
    const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  },

  /**
   * Store auth token and user info
   */
  setAuth: (token, userInfo) => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
  },

  /**
   * Clear auth data
   */
  clearAuth: () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  },

  /**
   * Get redirect URI (consistent across all OAuth flows)
   */
  getRedirectUri: () => {
    return window.location.origin;
  },

  /**
   * Redirect to Cognito login
   */
  login: () => {
    const redirectUri = authService.getRedirectUri();
    const loginUrl = `https://${COGNITO_DOMAIN}/oauth2/authorize?client_id=${CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    console.log('Redirecting to Cognito login with redirect_uri:', redirectUri);
    console.log('Full login URL:', loginUrl);
    window.location.href = loginUrl;
  },

  /**
   * Logout and redirect to Cognito logout
   */
  logout: () => {
    authService.clearAuth();
    window.location.href = `https://${COGNITO_DOMAIN}/logout?client_id=${CLIENT_ID}&logout_uri=${encodeURIComponent(authService.getRedirectUri())}`;
  },

  /**
   * Exchange authorization code for tokens
   * @param {string} code - Authorization code from URL
   * @returns {Promise<{token: string, userInfo: Object}>}
   */
  exchangeCodeForTokens: async (code) => {
    const redirectUri = authService.getRedirectUri();
    console.log('Exchanging code for tokens with redirect_uri:', redirectUri);
    
    const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        code: code,
        redirect_uri: redirectUri
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', response.status, errorText);
      throw new Error(`Failed to exchange code for tokens: ${response.status} ${errorText}`);
    }

    const tokens = await response.json();
    const token = tokens.id_token;

    // Decode JWT to get user info
    const payload = JSON.parse(atob(token.split('.')[1]));
    const userInfo = {
      email: payload.email,
      sub: payload.sub
    };

    return { token, userInfo };
  },

  /**
   * Decode JWT token
   * @param {string} token - JWT token
   * @returns {Object} Decoded payload
   */
  decodeToken: (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }
};

